import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, LayoutDashboard, ClipboardList, IndianRupee, BarChart3, Paperclip, LineChart } from "lucide-react";
import { useState, useEffect } from "react";

const HERO_BG = "https://customer-assets-cm19k8pv.emergentagent.net/job_workflow-manager-285/artifacts/y7hc83c8_ronaldcandonga-job-5382501_1920.jpg";

// SimpleIcons CDN — colored brand marks (marquee will greyscale via CSS)
const LOGOS = [
  { slug: "microsoft", label: "Microsoft" },
  { slug: "paypal", label: "PayPal" },
  { slug: "stripe", label: "Stripe" },
  { slug: "razorpay", label: "Razorpay" },
  { slug: "notion", label: "Notion" },
  { slug: "figma", label: "Figma" },
  { slug: "slack", label: "Slack" },
  { slug: "linear", label: "Linear" },
  { slug: "airbnb", label: "Airbnb" },
  { slug: "shopify", label: "Shopify" },
  { slug: "spotify", label: "Spotify" },
  { slug: "zoom", label: "Zoom" },
];

const FEATURES = [
  { title: "Projects that keep pace.", copy: "Kanban and table views, budget-vs-spend on every card, and one-click work-order spin-off for the freelancer already in the room.", shot: "/shot-projects.jpg", icon: LayoutDashboard, tone: "light" },
  { title: "Work orders, invoiced in seconds.", copy: "Assign, review and convert into an invoice from the same row. Freelancers only ever see their own. Always.", shot: "/shot-work-orders.jpg", icon: ClipboardList, tone: "dark" },
  { title: "Payouts in ₹, mandate-ready.", copy: "Pending, Partially Paid, Paid — with an INR invoice PDF branded in your green. Monthly autopay with e-mandate.", shot: "/shot-payments.jpg", icon: IndianRupee, tone: "gold" },
  { title: "Utilisation you can feel.", copy: "An eight-week heatmap surfaces the overbooked ones before Friday hits. Export CSV to close the loop with finance.", shot: "/shot-analytics.jpg", icon: BarChart3, tone: "light" },
];

const POWERED = [
  { title: "Role-based access", copy: "SuperAdmin, Manager, Freelancer. Row-level scoping — not just hidden buttons.", icon: LayoutDashboard },
  { title: "Drag-drop attachments", copy: "Real object storage, XSS-safe delivery, invoiced with the same click.", icon: Paperclip },
  { title: "Performance scorecards", copy: "Quality × on-time × revisions per work order. Leaderboard rolls up automatically.", icon: LineChart },
  { title: "Client Portal", copy: "Read-only project dashboards for your clients with a one-click approval flow.", icon: IndianRupee },
];

const TESTIMONIALS = [
  { quote: "We were using multiple tools to manage projects, freelancers, and client requests. Having everything centralized has made our day-to-day operations significantly more efficient.", name: "James Anderson", role: "Head of Operations, Digital Services — United Kingdom", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=faces&auto=format&q=80" },
  { quote: "The platform gives us complete visibility across projects, assignments, freelancers, and workloads. It has helped our team create a much more structured and accountable workflow.", name: "Arjun Krishnan", role: "Business Operations Head, Technology Services — India", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=faces&auto=format&q=80" },
  { quote: "The client portal and workflow management features have made collaboration much smoother. Our team can manage client requests, assignments, and project updates without relying on multiple systems.", name: "Emily Carter", role: "Operations Director, Business Services — United Arab Emirates", avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=faces&auto=format&q=80" },
  { quote: "The platform has transformed the way we coordinate projects and manage our remote teams. The combination of workload tracking, performance insights, and client management has made our operations much more structured and efficient.", name: "Sophia Williams", role: "Head of Operations, Professional Services — Canada", avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop&crop=faces&auto=format&q=80" },
];

function Nav() {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-cream/85 border-b border-black/5" data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="font-display text-2xl leading-none tracking-tight" data-testid="landing-brand">FreLancTrac<span className="text-royal-700">.</span></Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-ink/70">
          <a href="#features" className="hover:text-ink">Features</a>
          <a href="#powered" className="hover:text-ink">Platform</a>
          <a href="#testimonials" className="hover:text-ink">Studios</a>
          <Link to="/plans" className="hover:text-ink">Pricing</Link>
        </nav>
        <div className="flex items-center gap-3">
          {user ? <Link to="/dashboard" className="btn-primary" data-testid="landing-goto-app">Log In →</Link> :
            <><Link to="/login" data-testid="landing-login" className="text-sm hover:text-royal-700">Log in</Link>
              <Link to="/signup" data-testid="landing-start" className="btn-primary">Get started</Link></>}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="landing-hero">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-royal-900/85 via-royal-900/70 to-royal-900/95" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 py-32 md:py-40 text-center text-cream">
        <h1 className="font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.98] mt-6">
          The operating system<br />for freelancer studios.
        </h1>
        <p className="mt-6 text-lg text-cream/80 max-w-2xl mx-auto">
          Projects, work orders, payouts, performance and utilisation — in one royal-green dashboard that stays out of your freelancers' way.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/signup" data-testid="hero-cta-start" className="btn-primary bg-gold text-ink hover:bg-gold inline-flex items-center gap-2">Get started <ArrowRight size={14} /></Link>
          <Link to="/login" data-testid="hero-cta-login" className="border border-cream/30 text-cream rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-cream/10">Sign in</Link>
        </div>
        <div className="mt-8 text-xs text-cream/70 flex flex-wrap justify-center gap-x-6 gap-y-2">
          <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-gold" /> INR-first invoices</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-gold" /> Row-level RBAC</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-gold" /> Cancel any month</span>
        </div>
      </div>
    </section>
  );
}

function Ribbon() {
  const line = [...LOGOS, ...LOGOS];
  return (
    <section className="py-14 border-b border-black/5 overflow-hidden bg-white" data-testid="landing-ribbon">
      <div className="max-w-7xl mx-auto px-6 mb-6 text-center">
        <div className="label-caps">Beloved by studios that also use</div>
      </div>
      <div className="relative">
        <div className="marquee-track items-center">
          {line.map((l, i) => (
            <img key={i} src={`https://cdn.simpleicons.org/${l.slug}`} alt={l.label}
              data-testid={`ribbon-logo-${i}`}
              className="h-10 md:h-12 opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition duration-300"
              onError={(e) => { e.target.style.display = "none"; }} />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent" />
      </div>
    </section>
  );
}

function FeatureBlock({ f, i }) {
  const Icon = f.icon;
  const isDark = f.tone === "dark";
  const isGold = f.tone === "gold";
  const bg = isDark ? "bg-royal-800 text-cream" : isGold ? "bg-[linear-gradient(135deg,#F7EED0_0%,#E5D7B4_60%,#B8974A_100%)] text-ink" : "bg-white text-ink";
  return (
    <div data-testid={`feature-block-${i}`}
      className={`relative rounded-[2.5rem] p-8 md:p-12 card-lift overflow-hidden border ${isDark ? "border-white/10" : "border-black/5"} ${bg}`}>
      <div className={`flex items-center gap-3 label-caps ${isDark ? "text-cream/70" : ""}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? "bg-gold text-ink" : "bg-royal-800 text-cream"}`}>
          <Icon size={14} />
        </div>
        <span className="font-mono-tight">Feature {String(i + 1).padStart(2, "0")}</span>
      </div>
      <h3 className={`font-display text-3xl md:text-4xl mt-5 tracking-tight leading-tight ${isDark ? "" : "text-royal-800"}`}>{f.title}</h3>
      <p className={`mt-4 text-base leading-relaxed max-w-md ${isDark ? "text-cream/80" : "text-ink/70"}`}>{f.copy}</p>
      <div className={`mt-8 rounded-[1.5rem] overflow-hidden border shadow-lg ${isDark ? "border-white/10" : "border-black/10"}`}>
        <img src={f.shot} alt={f.title} className="w-full block" onError={(e) => (e.target.style.display = "none")} />
      </div>
    </div>
  );
}

function Powered() {
  return (
    <section id="powered" className="py-24 bg-white" data-testid="landing-powered">
      <div className="max-w-7xl mx-auto px-6 text-center max-w-2xl mx-auto">
        <div className="label-caps">Powered by the FreLancTrac platform</div>
        <h2 className="font-display text-4xl md:text-5xl mt-3 text-royal-800">Everything a studio needs.<br />Nothing a freelancer shouldn't see.</h2>
      </div>
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-4 mt-12">
        {POWERED.map((p, i) => {
          const Icon = p.icon; const green = i === 1; const gold = i === 3;
          return (
            <div key={i} data-testid={`powered-card-${i}`} className={`p-8 rounded-[1.75rem] card-lift ${green ? "bg-royal-800 text-cream" : gold ? "bg-gold text-ink" : "bg-cream-2 text-ink"}`}>
              <Icon size={22} strokeWidth={1.75} />
              <div className={`font-display text-xl mt-4 ${green ? "" : "text-royal-800"}`}>{p.title}</div>
              <div className={`mt-3 text-sm leading-relaxed ${green ? "text-cream/75" : "text-ink/70"}`}>{p.copy}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Testimonials() {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [anim, setAnim] = useState(false);
  useEffect(() => {
    const t = setInterval(() => { setDir(1); setAnim(true); setTimeout(() => { setI((n) => (n + 1) % TESTIMONIALS.length); setAnim(false); }, 320); }, 6000);
    return () => clearInterval(t);
  }, []);
  const go = (d) => { setDir(d); setAnim(true); setTimeout(() => { setI((n) => (n + d + TESTIMONIALS.length) % TESTIMONIALS.length); setAnim(false); }, 320); };
  const cur = TESTIMONIALS[i];
  const enterFrom = dir > 0 ? "translate-x-8" : "-translate-x-8";
  return (
    <section id="testimonials" className="py-24 bg-royal-900 text-cream" data-testid="landing-testimonials">
      <div className="max-w-7xl mx-auto px-6 text-center mb-12">
        <div className="label-caps text-cream/60">Loved by studios across India</div>
        <h2 className="font-display text-4xl md:text-5xl mt-3">Endorsed by the builders who ship.</h2>
      </div>
      <div className="max-w-4xl mx-auto px-6 relative">
        <div className={`bg-cream text-ink rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center transition-all duration-300 ${anim ? `opacity-0 ${enterFrom}` : "opacity-100 translate-x-0"}`}>
          <img src={cur.avatar} alt={cur.name} data-testid="testimonial-avatar"
            className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover ring-4 ring-gold shrink-0" />
          <div className="flex-1">
            <div className="font-display text-5xl text-royal-800 leading-none">"</div>
            <p data-testid="testimonial-quote" className="mt-2 text-lg md:text-xl leading-relaxed font-display text-royal-800">{cur.quote}</p>
            <div className="mt-6">
              <div className="font-semibold text-ink" data-testid="testimonial-name">{cur.name}</div>
              <div className="text-sm text-royal-700" data-testid="testimonial-role">{cur.role}</div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-6">
          <button data-testid="testimonial-prev" onClick={() => go(-1)} className="w-10 h-10 rounded-full bg-cream/10 border border-cream/25 flex items-center justify-center hover:bg-cream/20"><ArrowLeft size={16} /></button>
          {TESTIMONIALS.map((_, k) => (
            <button key={k} onClick={() => { setDir(k > i ? 1 : -1); setAnim(true); setTimeout(() => { setI(k); setAnim(false); }, 300); }}
              className={`w-2 h-2 rounded-full transition ${k === i ? "bg-gold w-6" : "bg-cream/30"}`} />
          ))}
          <button data-testid="testimonial-next" onClick={() => go(1)} className="w-10 h-10 rounded-full bg-cream/10 border border-cream/25 flex items-center justify-center hover:bg-cream/20"><ArrowRight size={16} /></button>
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  const plans = [
    { key: "essential", name: "Essential", tag: "Start here", rows: [["SuperAdmin", 2500], ["Manager", 1500], ["Freelancer", 700]] },
    { key: "professional", name: "Professional", tag: "Most studios pick this", rows: [["SuperAdmin", 10000], ["Manager", 4500], ["Freelancer", 700]], featured: true },
  ];
  return (
    <section id="pricing" className="py-24 bg-cream" data-testid="landing-pricing-preview">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <div className="label-caps">Simple pricing · pay per seat, per month</div>
        <h2 className="font-display text-4xl md:text-5xl mt-3 text-royal-800">Two plans. No surprises.</h2>
        <p className="mt-4 text-ink/70">Bring the exact team you have. Managers and freelancers can be added or removed any time.</p>
      </div>
      <div className="max-w-5xl mx-auto px-6 mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {plans.map((p) => (
          <div key={p.key} data-testid={`plan-preview-${p.key}`}
            className={`rounded-[1.75rem] p-8 card-lift border ${p.featured ? "bg-royal-800 text-cream border-royal-800" : "bg-white text-ink border-black/5"}`}>
            <div className={`label-caps ${p.featured ? "text-gold" : ""}`}>{p.tag}</div>
            <div className={`font-display text-4xl mt-2 ${p.featured ? "" : "text-royal-800"}`}>{p.name}</div>
            <ul className="mt-6 space-y-3 text-sm">
              {p.rows.map(([role, rate]) => (
                <li key={role} className={`flex justify-between border-b ${p.featured ? "border-white/10" : "border-black/5"} pb-2`}>
                  <span className="opacity-80">{role} <span className="opacity-60 text-xs">(per person)</span></span>
                  <span className="font-mono-tight">₹ {rate.toLocaleString("en-IN")}<span className="opacity-60 text-xs">/mo</span></span>
                </li>
              ))}
            </ul>
            <Link to="/plans" data-testid={`plan-preview-cta-${p.key}`}
              className={`mt-8 inline-flex items-center gap-2 ${p.featured ? "bg-gold text-ink" : "btn-primary"} rounded-full px-5 py-2.5 font-semibold text-sm`}>
              Choose {p.name} <ArrowRight size={14} />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-cream text-ink">
      <Nav />
      <Hero />
      <Ribbon />

      <section id="features" className="py-24 max-w-7xl mx-auto px-6" data-testid="landing-features">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <div className="label-caps">What FreLancTrac replaces</div>
          <h2 className="font-display text-4xl md:text-5xl mt-3 text-royal-800">Spreadsheets, group chats, and half a dozen tabs.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => <FeatureBlock key={i} f={f} i={i} />)}
        </div>
      </section>

      <Powered />
      <Testimonials />
      <PricingPreview />

      <footer className="bg-ink text-cream/70 border-t border-gold/20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="font-display text-3xl text-cream">FreLancTrac<span className="text-gold">.</span></div>
              <p className="text-sm mt-3 text-cream/60 max-w-sm leading-relaxed">
                A production suite built for creative studios — projects, freelancers, invoices, and autopay in one clean workspace.
              </p>
            </div>
            <div>
              <div className="text-cream text-sm font-semibold mb-4">Product</div>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-gold transition-colors">Features</a></li>
                <li><Link to="/plans" className="hover:text-gold transition-colors">Pricing</Link></li>
                <li><a href="#testimonials" className="hover:text-gold transition-colors">Studios</a></li>
                <li><Link to="/login" className="hover:text-gold transition-colors">Log in</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-cream text-sm font-semibold mb-4">Company</div>
              <ul className="space-y-2 text-sm">
                <li>Oscorp</li>
                <li><a href="mailto:support@frelanctrac.com" className="hover:text-gold transition-colors">support@frelanctrac.com</a></li>
                <li className="text-cream/50">Bengaluru, India</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-cream/10 flex flex-col md:flex-row justify-between gap-3 text-xs text-cream/50">
            <div>© {new Date().getFullYear()} Oscorp. All rights reserved.</div>
            <div className="flex gap-5">
              <a href="#" className="hover:text-cream">Privacy</a>
              <a href="#" className="hover:text-cream">Terms</a>
              <a href="#" className="hover:text-cream">Refunds</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
